import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { ScanFilterStateService } from "src/app/services/scan-filter-state.service";
import { BreadcrumbService } from "src/app/services/breadcrumb.service";
import {
  ProductIntelligenceService,
  ProductIntelligenceRequest,
  ProductIntelligenceInsights,
  ProductIntelligenceReportItem,
  ProductRatingFeedItem,
} from "src/app/services/home/product-intelligence.service";
import { apiErrorMessage, reportErrorState } from "src/app/shared/api-error.util";

@Component({
  selector: "app-product-intelligence",
  templateUrl: "./product-intelligence.component.html",
  styleUrls: ["./product-intelligence.component.css"],
})
export class ProductIntelligenceComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  breadcrumb = ["Dashboard", "Product Intelligence"];
  isLightMode = document.body.classList.contains("light-mode");

  compId = 0;

  // ===================== API-backed state =====================
  insights: ProductIntelligenceInsights | null = null;
  reportItems: ProductIntelligenceReportItem[] = [];
  latestRatings: ProductRatingFeedItem[] = [];
  selectedProduct: ProductIntelligenceReportItem | null = null;
  loading = true;
  loadError = "";
  /** Set alongside loadError, drives the full-page ReportErrorStateComponent
   *  (see showFullPageError) - null while there's no error. */
  loadErrorState: { title: string; message: string } | null = null;
  isExporting = false;

  private dateFrom: string | null = null;
  private dateTo: string | null = null;
  private dateRangeSub: Subscription | null = null;
  /** True once the initial date-range value has been applied - see
   *  ngOnInit's comment (same reasoning as generated-qr-report.component.ts). */
  private initialized = false;

  // ===================== Search =====================
  searchTerm = "";
  private searchDebounceTimer: any = null;

  readonly filterCategoryOptions: string[] = [];
  readonly filterRiskOptions: string[] = ["Low", "Medium", "High"];
  readonly filterStatusOptions: string[] = ["Active", "Inactive"];
  pendingCategoryFilter: string | null = null;
  pendingRiskFilter: string | null = null;
  pendingStatusFilter: string | null = null;
  appliedCategoryFilter: string | null = null;
  appliedRiskFilter: string | null = null;
  appliedStatusFilter: string | null = null;
  isFilterDrawerOpen = false;
  /** Which of the drawer's own Category/Risk/Status dropdowns is open, or none. */
  openDropdown: "category" | "risk" | "status" | null = null;

  // ===================== Table (server-paginated) =====================
  rows = 8;
  rowsPerPageOptions = [8, 10, 25, 50];
  currentPage = 1;
  totalRecords = 0;
  totalPages = 1;

  private themeChangeHandler = (): void => {
    this.isLightMode = document.body.classList.contains("light-mode");
  };

  private filtersBtnClickHandler = (e: any): void => {
    if (
      e.target &&
      e.target.closest &&
      e.target.closest("#filtersTriggerBtn")
    ) {
      e.stopPropagation();
      this.openFilterDrawer();
    }
  };

  private exportBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#exportBtn")) {
      e.stopPropagation();
      this.exportTable();
    }
  };

  private syncBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#syncBtn")) {
      e.stopPropagation();
      this.refreshData();
    }
  };

  constructor(
    private router: Router,
    private scanFilterState: ScanFilterStateService,
    public breadcrumbService: BreadcrumbService,
    private productIntelligenceService: ProductIntelligenceService,
  ) {}

  /** Every crumb but the last points back to the dashboard - same reasoning
   *  as HeaderComponent.goToDashboard(), now that the breadcrumb is rendered
   *  in the page topbar instead of the navbar. */
  goToDashboard(): void {
    this.router.navigate(["/dashboard1"]);
  }

  /** Consumer Voice "View all" - the full feedback report. Kept as a real
   *  href so middle-click / open-in-new-tab still work. */
  goToFeedbackReport(event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0)
      return;
    event.preventDefault();
    this.router.navigate(["/reports/feedbackrpt"]);
  }

  ngOnInit(): void {
    this.breadcrumbService.items = this.breadcrumb;

    const userData = localStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      this.compId = user.compId;
    }

    this.dateRangeSub = this.scanFilterState.dateRange$.subscribe((range) => {
      this.dateFrom = range.dateFrom;
      this.dateTo = range.dateTo;
      this.currentPage = 1;
      if (this.initialized) this.fetchData();
    });

    this.initialized = true;
    this.fetchData();
  }

  ngAfterViewInit(): void {
    document.addEventListener("click", this.filtersBtnClickHandler);
    document.addEventListener("click", this.exportBtnClickHandler);
    document.addEventListener("click", this.syncBtnClickHandler);
    window.addEventListener("sa-theme-change", this.themeChangeHandler);
    this.updateHeaderFiltersBadge();
  }

  ngOnDestroy(): void {
    document.removeEventListener("click", this.filtersBtnClickHandler);
    document.removeEventListener("click", this.exportBtnClickHandler);
    document.removeEventListener("click", this.syncBtnClickHandler);
    window.removeEventListener("sa-theme-change", this.themeChangeHandler);
    if (this.dateRangeSub) this.dateRangeSub.unsubscribe();
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.breadcrumbService.items = [];
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.openDropdown = null;
  }

  toggleDropdown(which: "category" | "risk" | "status", ev: Event): void {
    ev.stopPropagation();
    this.openDropdown = this.openDropdown === which ? null : which;
  }

  /** Search relies entirely on the backend: the debounced call below sends
   *  `searchTerm` as the API's search param and the server-paginated
   *  response fully drives the table - there is no client-side filtering
   *  here. Debounced so a re-fetch only fires once the user pauses typing. */
  onSearch(term: string): void {
    this.searchTerm = term;
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.fetchData();
    }, 400);
  }

  private updateHeaderFiltersBadge(): void {
    const countEl = document.getElementById("filtersCount");
    const btn = document.getElementById("filtersTriggerBtn");
    if (!countEl || !btn) return;
    const active = [
      this.appliedCategoryFilter,
      this.appliedRiskFilter,
      this.appliedStatusFilter,
    ].filter(Boolean).length;
    countEl.textContent = String(active);
    btn.classList.toggle("has-active", active > 0);
  }

  openFilterDrawer(): void {
    this.pendingCategoryFilter = this.appliedCategoryFilter;
    this.pendingRiskFilter = this.appliedRiskFilter;
    this.pendingStatusFilter = this.appliedStatusFilter;
    this.isFilterDrawerOpen = true;
  }

  closeFilterDrawer(): void {
    this.isFilterDrawerOpen = false;
  }

  applyDrawerFilters(): void {
    this.appliedCategoryFilter = this.pendingCategoryFilter;
    this.appliedRiskFilter = this.pendingRiskFilter;
    this.appliedStatusFilter = this.pendingStatusFilter;
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
  }

  clearDrawerFilters(): void {
    this.pendingCategoryFilter = null;
    this.pendingRiskFilter = null;
    this.pendingStatusFilter = null;
    this.appliedCategoryFilter = null;
    this.appliedRiskFilter = null;
    this.appliedStatusFilter = null;
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
  }

  /** Calls the real Product Intelligence API. Server-paginated - reportItems
   *  is only the current page's rows, with totalRecords/totalPages
   *  describing the full result set. There is no local fallback data: on
   *  failure the table/insights stay in their empty state and loadError
   *  drives an inline error message in the template instead. */
  private fetchData(): void {
    this.loading = true;
    this.loadError = "";
    this.loadErrorState = null;

    const request: ProductIntelligenceRequest = {
      compId: this.compId,
      fromDate: this.dateFrom,
      toDate: this.dateTo,
      pageNo: this.currentPage,
      pageSize: this.rows,
      search: this.searchTerm.trim() || null,
    };

    this.productIntelligenceService.getProductIntelligence(request).subscribe({
      next: (res) => {
        this.loading = false;
        this.insights = res.insights || null;
        this.reportItems = Array.isArray(res.data) ? res.data : [];
        this.latestRatings = Array.isArray(res.latestRatings)
          ? res.latestRatings
          : [];
        this.totalRecords = res.totalRecords || 0;
        this.totalPages = res.totalPages || 1;
        if (res.pageNo) this.currentPage = res.pageNo;
        if (res.pageSize) this.rows = res.pageSize;

        // Keep the same product selected across a refetch if it's still on
        // this page, otherwise fall back to the first row.
        const stillSelected =
          this.selectedProduct &&
          this.reportItems.find(
            (r) => r.productId === this.selectedProduct!.productId,
          );
        this.selectedProduct = stillSelected || this.reportItems[0] || null;
      },
      error: (error) => {
        console.error("GetProductIntelligence error:", error);
        this.loading = false;
        this.loadError = apiErrorMessage(error);
        this.loadErrorState = reportErrorState(error);
      },
    });
  }

  refreshData(): void {
    const syncBtn = document.getElementById("syncBtn");
    if (syncBtn) {
      syncBtn.classList.add("is-syncing");
      setTimeout(() => syncBtn.classList.remove("is-syncing"), 700);
    }
    this.fetchData();
  }

  /** True whenever the latest load attempt failed - shows the full-page
   *  ReportErrorStateComponent in place of the report content, even if a
   *  previous page of data is still sitting in memory. */
  get showFullPageError(): boolean {
    return !!this.loadErrorState;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.rows + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.rows, this.totalRecords);
  }

  get pageNumbers(): (number | "...")[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: (number | "...")[] = [1];
    if (current - 1 > 2) pages.push("...");
    for (
      let p = Math.max(2, current - 1);
      p <= Math.min(total - 1, current + 1);
      p++
    ) {
      pages.push(p);
    }
    if (current + 1 < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  }

  goToPage(page: number | "..."): void {
    if (page === "..." || page === this.currentPage) return;
    this.currentPage = page;
    this.fetchData();
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchData();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchData();
    }
  }

  onRowsChange(rows: number): void {
    this.rows = rows;
    this.currentPage = 1;
    this.fetchData();
  }

  /** Row click / a KPI card's "View ... →" link all funnel here - every one
   *  of them means "show me this product". */
  selectProduct(p: ProductIntelligenceReportItem): void {
    this.selectedProduct = p;
    const row = document.getElementById("pi-product-row-" + p.productId);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /** The KPI cards name a product (Top Scanned / Most Trusted / Fastest
   *  Growing) from the company-wide insights, which may or may not be on
   *  the currently loaded report page - selects it if found, no-ops
   *  otherwise rather than firing another request just to chase it down. */
  selectByName(name: string | null | undefined): void {
    if (!name) return;
    const match = this.reportItems.find((r) => r.productName === name);
    if (match) this.selectProduct(match);
  }

  starsArray(rating: number): number[] {
    return Array(5)
      .fill(0)
      .map((_, i) => (i < Math.round(rating) ? 1 : 0));
  }

  /** "3.17/5 (10 ratings)" - the table's own star row hover tooltip. */
  ratingTooltip(rating: number, count: number): string {
    const noun = count === 1 ? "rating" : "ratings";
    return `${rating}/5 (${count} ${noun})`;
  }

  /** "Jul 9, 2025 10:15 AM" style timestamp for Last Verified / ratings feed. */
  formatDateTime(iso: string | null | undefined): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const datePart = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  }

  /** "8.32Cr" / "4.12L" / "102.4K" style - used for the large verification
   *  counts. Indian numbering (lakh/crore), not the M/K short-scale — 1L =
   *  1,00,000, 1Cr = 1,00,00,000. */
  compact(n: number | null | undefined): string {
    if (n == null) return "-";
    if (n >= 1_00_00_000)
      return (n / 1_00_00_000).toFixed(2).replace(/\.00$/, "") + "Cr";
    if (n >= 1_00_000)
      return (n / 1_00_000).toFixed(2).replace(/\.00$/, "") + "L";
    if (n >= 1_000) return (n / 1_000).toFixed(2).replace(/\.00$/, "") + "K";
    return String(n);
  }

  // ===================== Cross-page integration - reuses the same
  // ScanFilterStateService the dashboard and analytics.component.ts already
  // share, so "View Full Intelligence" lands on a pre-filtered drill-down
  // instead of a generic unfiltered one. =====
  investigateProduct(): void {
    if (!this.selectedProduct) return;
    this.scanFilterState.product = this.selectedProduct.productName;
    this.scanFilterState.status = null;
    this.router.navigate(["/analytics/suspected"]);
  }

  /** The on-screen table only ever holds one server-paginated page - export
   *  re-fetches the same filtered request (date range/search) with
   *  pageSize: -1 so the CSV covers every matching record, not just
   *  whatever page happens to be loaded. Same -1-means-all convention
   *  ProductMasterService.loadProductsForDdl and the other reports'
   *  exportTable() already use. */
  exportTable(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    const request: ProductIntelligenceRequest = {
      compId: this.compId,
      fromDate: this.dateFrom,
      toDate: this.dateTo,
      search: this.searchTerm.trim() || null,
      pageNo: 1,
      pageSize: -1,
    };
    this.productIntelligenceService.getProductIntelligence(request).subscribe({
      next: (res) => {
        this.isExporting = false;
        this.downloadCsv(res.data || []);
      },
      error: (error) => {
        console.error("Export failed to load full report data", error);
        this.isExporting = false;
        this.downloadCsv(this.reportItems);
      },
    });
  }

  private downloadCsv(data: ProductIntelligenceReportItem[]): void {
    const headers = [
      "Rank",
      "Product",
      "Product Code",
      "Total Verifications",
      "Authentic %",
      "Product Rating",
      "Rating Count",
      "Growth %",
    ];
    const rows = data.map((p) => [
      String(p.rank),
      p.productName,
      p.productCode,
      String(p.totalVerifications),
      `${p.authenticPercentage}%`,
      String(p.productRating),
      String(p.ratingCount),
      `${p.growthPercentage}%`,
    ]);
    const escapeCsv = (val: string) => `"${(val || "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((cols) => cols.map(escapeCsv).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product-intelligence.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
